import React, { useEffect, useState } from 'react';

// Hooks
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { failure } from '~/redux/actions/snackbar-actions';

//Utils
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION } from '~/utils/constants';
import { createEnvelopeProofOrder, createViewProofForOrder } from '~/utils/template-builder';
import { MESSAGES } from '~/utils/message';
import { stringLength } from '~/utils/helperFunctions';

// Components
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import OlcBulkSelectionDropdown from '~/components/General/OlcBulkSelectionDropdown';

// MUI Components
import DataGrid from '~/components/General/OlcDataGrid';
import {
  Box,
  MenuItem,
  Typography,
  Tooltip,
  CircularProgress,
  Stack,
  IconButton,
  Menu,
  Checkbox,
} from '@mui/material';

// Icons
import {
  DispatchCIcon,
  NoContactCIcon,
  DownloadCIcon,
  AdminOrderCancelCIcon,
  InfoCIcon,
} from '~/assets/images';
import DoneIcon from '@mui/icons-material/Done';
import RemoveIcon from '@mui/icons-material/Remove';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MailIcon from '@mui/icons-material/MailOutlined';

// Styles
import './styles.scss';

const productTypeStyles = {
  overflow: 'auto !important',
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  '@media (width < 1450px)': {
    maxWidth: '100%',
  },
};

const OrdersTable = ({
  pagination,
  updatePagination,
  setOpenActionModal,
  setDispatchModal,
  setRescheduleModalOpen,
  setSelectedOrder,
  searchApplied,
  setSelectedRows,
  selectedRows,
  activeRow,
  setActiveRow,
}) => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [downloadingProof, setDownloadingProof] = useState({});
  const [downloadingEnvelopeProof, setDownloadingEnvelopeProof] = useState({});
  const [bulkSelect, setBulkSelect] = useState(false);
  const [removeBulkSelect, setRemoveBulkSelect] = useState(false);
  const [rows, setRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showActions, setShowActions] = useState(null);
  const openActions = Boolean(showActions);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setShowActions(event.currentTarget);
  };
  const handleClose = () => {
    setShowActions(null);
  };

  const pageSizes = DEFAULT_PAGE_SIZES;

  const fetchedRows = useSelector((state) => state.adminOrdersReducers.orders);

  const rowsCount = useSelector((state) => state.adminOrdersReducers.rowsCount);
  const to = useSelector((state) => state.adminOrdersReducers.to);
  const from = useSelector((state) => state.adminOrdersReducers.from);
  const lastPage = useSelector((state) => state.adminOrdersReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.adminOrdersReducers.currentPage || 1);
  const perPage = useSelector(
    (state) => state.adminOrdersReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const isLoading = useSelector((state) => state.adminOrdersReducers.loading);

  const dispatch = useDispatch();

  const downloadViewProof = async (params) => {
    const orderId = params.orderId;
    if (downloadingProof[orderId]) return;

    setDownloadingProof((prev) => ({
      ...prev,
      [orderId]: true,
    }));

    try {
      await dispatch(
        createViewProofForOrder(
          `Order-${orderId}`,
          {
            orderId: orderId,
          },
          'admin-order',
          activeRow?.templateType
        )
      );

      setDownloadingProof((prev) => ({
        ...prev,
        [orderId]: false,
      }));
      handleClose();
    } catch (error) {
      setDownloadingProof((prev) => ({
        ...prev,
        [orderId]: false,
      }));
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    }
  };

  const handleChangePage = (event, newPage) => {
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const selectAll = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((order) => ({ ...order, checked: true }));
    setRows(updatedRows);
    setSelectedRows([-1]);
  };

  const selectVisble = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((order) => ({ ...order, checked: true }));
    setRows(updatedRows);

    const selectedRowIds = updatedRows.map((order) => order.orderId);
    setSelectedRows(selectedRowIds);
  };

  const selectNone = () => {
    setBulkSelect(false);
    setRemoveBulkSelect(false);
    const updatedRows = rows.map((order) => ({ ...order, checked: false }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  const selectRow = (event, selectedRow) => {
    event.stopPropagation();
    // Update the selectedRow object directly in the rows array
    const updatedRows = rows.map((row) =>
      row.orderId === selectedRow.orderId ? { ...row, checked: !row.checked } : row
    );

    // If the user selects all orders and then removes a single order, update the selected rows array accordingly.
    if (selectedRows[0] === -1) {
      const filteredRows = rows.map((row) =>
        row.orderId === selectedRow.orderId
          ? { ...row, checked: !row.checked }
          : { ...row, checked: true }
      );

      const filteredRowsIds = filteredRows
        .map((order) => order.checked === true && order.orderId)
        .filter(Boolean);

      setSelectedRows(filteredRowsIds);
      setRows(updatedRows);
      setRemoveBulkSelect(true);
      return;
    }

    setRows(updatedRows);

    const selectedRowIds = selectedRows.includes(selectedRow.orderId)
      ? selectedRows.filter((id) => id !== selectedRow.orderId) // Remove the ID if already selected
      : [...selectedRows, selectedRow.orderId]; // Add the ID if not selected

    // Update the selectedRows state with the new array
    setSelectedRows(selectedRowIds);

    setRemoveBulkSelect(true);
  };

  const bulkSelection = () => {
    const newBulkSelect = !bulkSelect;
    setBulkSelect(newBulkSelect);

    const updatedRows = rows.map((order) => ({
      ...order,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);

    const selectedRowIds = updatedRows.map((order) => order.orderId);
    newBulkSelect ? setSelectedRows(selectedRowIds) : setSelectedRows([]);
  };

  const removeBulkSelection = () => {
    const newBulkSelect = !removeBulkSelect;
    setRemoveBulkSelect(newBulkSelect);
    setBulkSelect(newBulkSelect);

    const updatedRows = rows.map((order) => ({
      ...order,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  const CreatedByTooltip = ({ createdBy, userId }) => {
    return (
      <Box className="createdByTooltip">
        <Typography>
          <span>ID:</span> {userId} | <span>Name:</span> {createdBy}
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [currentPage, perPage]);

  useEffect(() => {
    !selectedRows.length && setRemoveBulkSelect(false);
    (rows.length && selectedRows.length === rows.length) || selectedRows[0] === -1
      ? (setBulkSelect(true), setRemoveBulkSelect(false))
      : setBulkSelect(false);
  }, [selectedRows, rows]);

  useEffect(() => {
    setRows(fetchedRows);
  }, [fetchedRows]);

  const downloadEnvelopeProof = async (params) => {
    try {
      setDownloadingEnvelopeProof((prev) => ({
        ...prev,
        [params?.orderId]: true,
      }));
      let payload = {
        orderId: params?.orderId,
      };
      await dispatch(createEnvelopeProofOrder(`Order-${params?.orderId}-Envelope-Proof`, payload));
      handleClose();
    } catch (error) {
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    } finally {
      setDownloadingEnvelopeProof((prev) => ({
        ...prev,
        [params?.orderId]: false,
      }));
    }
  };

  const OrderActions = (activeRow) => {
    const itemsArray = [];
    const hasEnvelope = activeRow?.product?.hasEnvelope;

    if (activeRow?.paymentStatus === 'Pending' && activeRow?.status === 'Scheduled') {
      itemsArray.push(
        <MenuItem
          onClick={() => {
            setSelectedOrder(activeRow?.orderId);
            setDispatchModal(true);
            handleClose();
          }}
          key={`dispatch-${activeRow?.orderId}`}
        >
          <img src={DispatchCIcon} alt="Dispatch Now" />
          Dispatch Now
        </MenuItem>
      );
    }

    // Add Reschedule action for Scheduled or Processing orders
    if (
      // (activeRow?.status === "Scheduled" || activeRow?.status === "Processing") &&
      activeRow?.status !== 'Canceled' &&
      activeRow?.paymentStatus !== 'Paid'
    ) {
      itemsArray.push(
        <MenuItem
          onClick={() => {
            setSelectedOrder(activeRow?.orderId);
            setRescheduleModalOpen(true);
            handleClose();
          }}
          key={`reschedule-${activeRow?.orderId}`}
        >
          <AccessTimeIcon
            sx={{
              width: '22px !important',
              height: '22px !important',
              transform: 'rotate(90deg)',
              fill: '#545454',
            }}
          />
          Reschedule
        </MenuItem>
      );
    }

    if (
      activeRow?.status === 'Processing' ||
      (activeRow?.status === 'Scheduled' && activeRow?.paymentStatus === 'Paid')
    ) {
      itemsArray.push(
        <MenuItem disabled key={`dispatched-${activeRow?.orderId}`}>
          <img
            src={DispatchCIcon}
            alt="Dispatched"
            style={{ opacity: '0.5', cursor: 'not-allowed' }}
          />
          Dispatched
        </MenuItem>
      );
    }

    if (activeRow?.status !== 'Canceled') {
      itemsArray.push(
        <MenuItem
          onClick={() => {
            if (!downloadingProof[activeRow?.orderId]) {
              downloadViewProof(activeRow);
            }
          }}
          disabled={downloadingProof[activeRow?.orderId]}
          key={`mailer-proof-${activeRow?.orderId}`}
        >
          {downloadingProof[activeRow?.orderId] ? (
            <CircularProgress
              sx={{
                color: '#ED5C2F',
                width: '20px !important',
                height: '20px !important',
              }}
            />
          ) : (
            <img src={DownloadCIcon} alt="Mailer Proof" />
          )}
          {downloadingProof[activeRow?.orderId] ? 'Downloading...' : 'Mailer Proof'}
        </MenuItem>
      );
    }

    if (activeRow?.status !== 'Canceled' && hasEnvelope) {
      itemsArray.push(
        <MenuItem
          onClick={() => downloadEnvelopeProof(activeRow)}
          disabled={downloadingEnvelopeProof[activeRow?.orderId]}
          key={`envelope-proof-${activeRow?.orderId}`}
        >
          {downloadingEnvelopeProof[activeRow?.orderId] ? (
            <CircularProgress
              sx={{
                color: '#ed5c2f !important',
                width: '19px !important',
                height: '19px !important',
              }}
            />
          ) : (
            <MailIcon fontSize="small" sx={{ color: '#303030', stroke: '#ffffff' }} />
          )}
          {downloadingEnvelopeProof[activeRow?.orderId] ? 'Downloading...' : 'Envelope Proof'}
        </MenuItem>
      );
    }

    if (
      activeRow?.paymentStatus !== 'Paid' &&
      (activeRow?.status === 'Scheduled' || activeRow?.paymentStatus !== 'Paid') &&
      activeRow?.status !== 'Canceled' &&
      activeRow?.status !== 'Processing'
    ) {
      itemsArray.push(
        <MenuItem
          onClick={() => {
            setSelectedOrder(activeRow?.orderId);
            setOpenActionModal(true);
            handleClose();
          }}
          key={`cancel-${activeRow?.orderId}`}
        >
          <img src={AdminOrderCancelCIcon} alt="Cancel" />
          Cancel Order
        </MenuItem>
      );
    }

    if (activeRow?.payloadBackupURL) {
      itemsArray.push(
        <MenuItem
          onClick={() => {
            window.open(activeRow?.payloadBackupURL, '_blank');
          }}
          key={`payload-backup-${activeRow?.orderId}`}
        >
          <img src={DownloadCIcon} alt="Download Payload" />
          Download Payload
        </MenuItem>
      );
    }

    return itemsArray.length ? itemsArray : false;
  };

  const columns = [
    {
      field: ' ',
      headerClassName: 'olcBulkSelectHeader',
      cellClassName: 'olcBulkSelectCell',
      renderHeader: () => (
        <Box className="checkboxHeader">
          {removeBulkSelect ? (
            <Checkbox
              className="bulkCheck"
              icon={<Box className="bulkNotchecked" />}
              checkedIcon={<RemoveIcon />}
              checked={removeBulkSelect}
              onClick={removeBulkSelection}
            />
          ) : (
            <Checkbox
              className="bulkCheck"
              icon={<Box className="bulkNotchecked" />}
              checkedIcon={<DoneIcon />}
              checked={bulkSelect}
              onClick={bulkSelection}
            />
          )}
          <OlcBulkSelectionDropdown
            ariaLabel="Open admin order selection options"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            open={open}
          />
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={() => setAnchorEl(null)}
            onClick={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            MenuListProps={{
              className: 'bulkMenuOptions',
            }}
          >
            <MenuItem
              onClick={rowsCount > 0 ? selectAll : undefined}
              className={selectedRows[0] === -1 ? 'highlight' : ''}
            >
              {MESSAGES.ADMIN_DASHBOARD.USERS.SELECT_ALL_ITEMS} ({rowsCount})
            </MenuItem>
            <MenuItem onClick={selectNone}>{MESSAGES.ADMIN_DASHBOARD.USERS.SELECT_NONE}</MenuItem>
            <MenuItem onClick={selectVisble}>
              {MESSAGES.ADMIN_DASHBOARD.USERS.SELECT_VISIBLE_ITEMS}
            </MenuItem>
          </Menu>
        </Box>
      ),
      width: 75,
      sortable: false,
      renderCell: (params) => (
        <Box className="checkboxRow">
          <Checkbox
            onClick={(event) => selectRow(event, params.row)}
            className="bulkCheck"
            icon={<Box className="bulkNotchecked" />}
            checkedIcon={<DoneIcon />}
            checked={params.row.checked}
          />
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      headerClassName: 'olcBulkSelectAdjacentHeader',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.orderId || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      width: 150,
      sortable: false,
      flex: window.innerWidth >= 1450 ? 1 : 0,
      renderCell: (params) =>
        stringLength(params.row.createdBy, 15) ? (
          <Tooltip
            title={<CreatedByTooltip createdBy={params.row.createdBy} userId={params.row.userId} />}
          >
            <Box className="createdByWrapper">
              <Link className="link" to={`/admin/users/${params.row.userId}`}>
                <Typography>{params.row.createdBy || ' -'}</Typography>
              </Link>
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title={`ID: ${params.row.userId}`}>
            <Box className="createdByWrapper">
              <Link className="link" to={`/admin/users/${params.row.userId}`}>
                <Typography>{params.row.createdBy || ' -'}</Typography>
              </Link>
            </Box>
          </Tooltip>
        ),
    },
    {
      field: 'batchId',
      headerName: 'Batch',
      type: 'number',
      width: 100,
      sortable: false,
      renderCell: (params) =>
        params.row.batchId ? (
          <Box className="addressWrapper">
            <Link className="link" to={`/admin/batches/${params.row.batchId}`}>
              <Typography>{params.row.batchId || ' -'}</Typography>
            </Link>
          </Box>
        ) : (
          <Box className="addressWrapper">
            <Typography>{' -'}</Typography>
          </Box>
        ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 150,
      height: 500,
      sortable: false,
      flex: window.innerWidth >= 1450 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.source || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Product',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper" sx={productTypeStyles}>
          <Typography>{params.row.productType || ' -'}</Typography>
          <Tooltip title={params.row.productTooltip}>
            <img src={InfoCIcon} alt="icon" />
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'quantity',
      headerName: 'QTY',
      width: 50,
      sortable: false,
      headerClassName: 'column-quantity',
      renderCell: (params) => (
        <Box className="valueWrapper">
          <Typography>{params.row.deliverableQuantity || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'revenue',
      headerName: 'Revenue',
      width: 150,
      sortable: false,
      headerClassName: 'column-revenue',
      flex: window.innerWidth >= 1450 ? 1 : 0,
      renderCell: (params) => (
        <Box className="valueWrapper">
          <Typography>{params.row.revenue || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'scheduledDate',
      headerName: 'Scheduled At',
      width: 150,
      sortable: false,
      flex: window.innerWidth >= 1450 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.scheduleDate || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'mailingDate',
      headerName: 'Mailing At',
      width: 150,
      sortable: false,
      flex: window.innerWidth >= 1450 ? 1 : 0,
      renderCell: (params) => (
        <Tooltip title={params.row.mailingDate}>
          <Box className="addressWrapper">
            <Typography>{params.row.mailingDate || ' -'}</Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'orderStatus',
      headerName: 'Status',
      width: 150,
      sortable: false,
      flex: window.innerWidth >= 1450 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.status || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment',
      width: 150,
      sortable: false,
      flex: window.innerWidth >= 1450 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.paymentStatus || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'Action',
      headerName: 'Action',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={OrderActions(params.row) ? null : 'No Actions Available'}>
            <IconButton
              aria-label="more"
              id="long-button"
              aria-controls={
                openActions && activeRow?.orderId === params.row.orderId ? 'long-menu' : undefined
              }
              aria-expanded={
                openActions && activeRow?.orderId === params.row.orderId ? 'true' : undefined
              }
              aria-haspopup="true"
              onClick={(event) => {
                OrderActions(params.row) ? setActiveRow(params.row) : undefined;
                OrderActions(params.row) ? handleClick(event) : event.stopPropagation();
              }}
              disableRipple={OrderActions(params.row) ? false : true}
              sx={{
                cursor: OrderActions(params.row) ? 'pointer' : 'not-allowed',
                opacity: OrderActions(params.row) ? '1' : '0.5',
              }}
            >
              <MoreVertIcon
                sx={{
                  color:
                    activeRow?.orderId === params.row.orderId && openActions
                      ? '#ed5c2f'
                      : 'inherit',
                }}
              />
            </IconButton>
          </Tooltip>
          <OlcActionMenu
            id="long-menu"
            MenuListProps={{
              'aria-labelledby': 'long-button',
            }}
            anchorEl={showActions}
            open={openActions && activeRow?.orderId === params.row.orderId}
            onClose={handleClose}
            key={activeRow?.id}
          >
            {OrderActions(activeRow)}
          </OlcActionMenu>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <div className="adminUserTable">
        <div className="adminOrderstableWrapper olcAdminListTable">
          <DataGrid
            rows={isLoading ? [] : rows}
            columns={columns}
            autoHeight={false}
            loading={isLoading}
            rowCount={rowsCount}
            pagination={pagination}
            paginationMode="server"
            hideFooterSelectedRowCount
            hideFooterPagination
            rowSelection={false}
            getRowId={(row) => row?.key}
            disableColumnMenu={true}
            className="adminOrdersTable"
            columnHeaderHeight={47}
            slots={{
              noRowsOverlay: () => (
                <Stack className="noRowsTextContacts">
                  <img src={NoContactCIcon} alt="NoContact" />
                  {`${MESSAGES.NO_RESULTS_TEXT} ${!searchApplied ? 'filters' : 'search'}`}
                </Stack>
              ),
              noResultsOverlay: () => (
                <Stack className="noRowsTextContacts">
                  <img src={NoContactCIcon} alt="NoContact" />
                  {MESSAGES.NO_ROWS_TEXT}
                </Stack>
              ),
            }}
          />
          {(isLoading || rowsCount > 0) && (
            <OlcTablePagination
              count={lastPage}
              page={currentPage}
              pageSize={perPage}
              pageSizes={pageSizes}
              onPageChange={handleChangePage}
              onPageSizeChange={(event) =>
                updatePagination({ page: 0, pageSize: event.target.value })
              }
              from={from}
              to={to}
              total={rowsCount}
              loading={isLoading}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default OrdersTable;
